import { ActionTree } from 'vuex'
import { RootState } from '@/store'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { OrderAPI } from '@/api/backend-client'
import { OrdersState, OrdersStateFilters } from '@/store/orders'
import { ReferralAge, ReferralFilterDirection, ReferralView } from '@/api/types'

export const actions: ActionTree<OrdersState, RootState> = {
    async fetchData({ getters, commit }): Promise<void> {
        try {
            commit('fetchData')
            commit('fetchDataSuccess', await OrderAPI.getPaged(getters.requestParams))

        } catch (error) {
            commit('fetchDataFailure', error)
        }
    },

    async setPage({ commit, dispatch }, payload: { page: number }): Promise<void> {
        commit('setPage', payload)
        dispatch('fetchData')
    },

    async nextPage({ state, commit, dispatch }): Promise<void> {
        commit('setPage', { page: state.page + 1 })
        dispatch('fetchData')
    },

    async prevPage({ state, commit, dispatch }): Promise<void> {
        commit('setPage', { page: state.page - 1 })
        dispatch('fetchData')
    },

    async setItemsPerPage({ commit, dispatch }, payload: { itemsPerPage: number }): Promise<void> {
        commit('setItemsPerPage', payload)
        commit('setPage', { page: 1 })
        dispatch('fetchData')
    },

    async applyView({ commit, dispatch }, payload: { view: ReferralView, direction: ReferralFilterDirection, update?: boolean }): Promise<void> {
        commit('applyView', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applySearch({ commit, dispatch }, payload: { query: string }): Promise<void> {
        commit('applySearch', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyOrder({ commit, dispatch }, payload: { field: string, direction: string }): Promise<void> {
        commit('applyOrder', payload)
        commit('setPage', { page: 1 })
        dispatch('fetchData')
    },

    async applyTypeFilter({ commit, dispatch }, payload: { typeFilter: string[] }): Promise<void> {
        commit('applyTypeFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyStatusFilter({ commit, dispatch }, payload: { statusFilter: string }): Promise<void> {
        commit('applyStatusFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyReferredByFilter({ commit, dispatch}, payload: { referredByFilter: User[], update?: boolean }): Promise<void> {
        commit('applyReferredByFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyReferredByBranchFilter({ commit, dispatch}, payload: { referredByBranchFilter: Branch[] }): Promise<void> {
        commit('applyReferredByBranchFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyAssignedToFilter({ commit, dispatch}, payload: { assignedToFilter: User[], update?: boolean }): Promise<void> {
        commit('applyAssignedToFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyAssignedToBranchFilter({ commit, dispatch}, payload: { assignedToBranchFilter: Branch[] }): Promise<void> {
        commit('applyAssignedToBranchFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyDateSpecificFilter({ commit, dispatch }, payload: { day: Date }): Promise<void> {
        commit('applyDateSpecificFilter', payload)
        commit('applyDateRangeFilter', { range: null })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyDateRangeFilter({ commit, dispatch }, payload: { range: string, update?: boolean }): Promise<void> {
        commit('applyDateRangeFilter', payload)
        commit('applyDateSpecificFilter', { day: null })

        if (payload.update ?? true) {
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyAgeFilter({ commit, dispatch }, payload: { ageFilter: ReferralAge }): Promise<void> {
        commit('applyStatusFilter', { statusFilter: null })
        commit('applyAgeFilter', payload)
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyFilters({ commit, dispatch }, payload: OrdersStateFilters): Promise<void> {
        commit('clearFilters')
        commit('applyFilters', payload)
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async resetFilters({ commit, dispatch }): Promise<void> {
        commit('resetFilters')
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async snapshot({ commit }): Promise<void> {
        commit('snapshot')
    },

    async updateMenuCount({ state, getters, commit }): Promise<void> {
        try {
            const counts = await OrderAPI.getOrderCountForType(state.typeFilter, getters.requestParams)
            commit('updateMenuCount')
            commit('updateMenuCountSuccess', counts)

        } catch (error) {
            commit('updateMenuCountFailure', error)
        }
    },
}
